// Where an announcement's button may send users (#1426). Both apps open the link in the browser,
// from a screen users may not be able to close, so it opens only our own site and our store pages,
// over https. The presets once sent everyone to status.weeknightbite.com, an unrelated blog's domain.
// firestore.rules checks the same pattern on globals/system_announcement, kept identical by
// tests/admin/announcementLink.test.js, so a link the CRM accepts is one the rules accept.
export const ANNOUNCEMENT_LINK_PATTERN =
  "^https://(([a-z0-9-]+[.])*weeknightbiteapp[.]com|apps[.]apple[.]com|play[.]google[.]com)([/?#].*)?$";

export const ANNOUNCEMENT_LINK_HOSTS = "weeknightbiteapp.com, apps.apple.com or play.google.com";

const LINK = new RegExp(ANNOUNCEMENT_LINK_PATTERN);
const OWN_DOMAIN = "weeknightbiteapp.com";
const STORE_HOSTS = ["apps.apple.com", "play.google.com"];

function refusal(reason) {
  return { ok: false, message: `${reason} The button opens only https links to ${ANNOUNCEMENT_LINK_HOSTS}.` };
}

/**
 * Checks a button link as typed. Returns { ok: true, url } with the link as it is written to
 * Firestore ("" for none; otherwise as the browser reads it, host in lower case, with a path), or
 * { ok: false, message }. Writing the parsed form matters: Android's Uri reads a raw
 * "https://weeknightbiteapp.com\@other.example" as a link to other.example.
 */
export function checkAnnouncementLink(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return { ok: true, url: "" };

  let url;
  try {
    url = new URL(raw);
  } catch {
    return refusal(`“${raw}” isn't a full link.`);
  }
  if (url.protocol !== "https:") return refusal(`The link starts with ${url.protocol}, not https:.`);

  const host = url.hostname;
  const ours = host === OWN_DOMAIN || host.endsWith(`.${OWN_DOMAIN}`) || STORE_HOSTS.includes(host);
  if (!ours) return refusal(`It can't open ${host}.`);
  if (url.username || url.password) return refusal("A link with a user name or password isn't allowed.");
  if (url.port) return refusal("A link with a port isn't allowed.");
  if (!LINK.test(url.href)) return refusal(`It can't open ${host}.`);
  return { ok: true, url: url.href };
}

/**
 * Checks the button's title and link together, as publishing writes them. A title without a link
 * draws a button that does nothing on iPhone, and a link without a title shows no button on either
 * app. Returns { ok: true, title, url } or { ok: false, field: "title" | "url", message }.
 */
export function checkAnnouncementButton(title, link) {
  const label = String(title ?? "").trim();
  const checked = checkAnnouncementLink(link);
  if (!checked.ok) return { ...checked, field: "url" };
  if (label && !checked.url) return { ok: false, field: "url", message: "Add the link the button opens, or clear the button title." };
  if (!label && checked.url) return { ok: false, field: "title", message: "Give the button a title, or clear its link." };
  return { ok: true, title: label, url: checked.url };
}
